import { format } from 'date-fns'
import { z } from 'zod/v3'

import type {
    ShopHomeItemInput,
    ShopHomeKind,
    ShopHomeSection,
    ShopHomeSectionInput,
} from '@/lib/api/shop-content-api'
import { newKey } from '@/components/retail/group-form-model'
import { m } from '@/lib/paraglide/messages'

// Trạng thái form một khối trang chủ (Bán lẻ › Trang chủ). Form có ĐÚNG hình
// body PUT /shop-admin/home/{kind} (data, items[].data…) nên params.field của
// lỗi 422 (vd items.0.data.link.slug) gắn thẳng vào ô, không cần đổi tên.
// Ô chữ luôn là chuỗi (trống = bỏ khi gửi); dòng có `key` riêng để kéo thả,
// bỏ khi gửi.

export interface ImageValue {
    url: string
    thumb_url?: string
}

export interface LinkForm {
    type: string
    slug: string
    url: string
    phone: string
    q: string
    categories: string[]
    /** Loại sản phẩm (khoá ?loai= của web) cho đích products; trống = không lọc loại. */
    types?: string[]
}

/** Loại sản phẩm của bộ lọc web `?loai=` (product-filters.ts PRODUCT_TYPES),
 *  cùng thứ tự với server (usecase/shop/product_types.go). */
export const PRODUCT_TYPE_KEYS = [
    'goi-12-5g',
    'goi',
    'bo-hop',
    'hop-su-go-kim-tuyen',
    'hop',
    'am-roi',
    'moc',
] as const

export function productTypeLabel(key: string): string {
    switch (key) {
        case 'goi-12-5g':
            return m.shop_product_type_goi_12_5g()
        case 'goi':
            return m.shop_product_type_goi()
        case 'bo-hop':
            return m.shop_product_type_bo_hop()
        case 'hop-su-go-kim-tuyen':
            return m.shop_product_type_hop_su_go_kim_tuyen()
        case 'hop':
            return m.shop_product_type_hop()
        case 'am-roi':
            return m.shop_product_type_am_roi()
        case 'moc':
            return m.shop_product_type_moc()
        default:
            return key
    }
}

export interface ItemForm {
    key: string
    id: string | null
    enabled: boolean
    show_web: boolean
    show_miniapp: boolean
    /** datetime-local (giờ máy); trống = luôn hiện. */
    starts_at: string
    ends_at: string
    data: Record<string, unknown>
}

export interface BlockForm {
    enabled: boolean
    show_web: boolean
    show_miniapp: boolean
    starts_at: string
    ends_at: string
    data: Record<string, unknown>
    items: ItemForm[]
}

export interface StatForm {
    key: string
    value: string
    label: string
}

export interface CertificateForm {
    key: string
    image: ImageValue | null
    title: string
    /** '' | '3' | '4' | '5' */
    stars: string
    /** '' | 'provincial' | 'national' */
    level: string
}

export interface StoreForm {
    key: string
    name: string
    address: string
    map_url: string
    phones: string[]
    website: string
    show_web: boolean
    show_miniapp: boolean
}

export interface BranchForm {
    key: string
    name: string
    show_web: boolean
    show_miniapp: boolean
    stores: StoreForm[]
}

/** Thứ tự loại đích trong ô chọn (hợp đồng link §14.20). */
export const LINK_TYPES = [
    'category',
    'group',
    'products',
    'search',
    'post',
    'page',
    'url',
    'tel',
    'zalo',
] as const

export type LinkType = (typeof LINK_TYPES)[number]

export const HERO_POSITIONS = [
    'center',
    'center top',
    'center 35%',
    'center bottom',
    'left center',
    '70% center',
    'right center',
] as const

// Giới hạn như server (usecase/shop/home_types.go).
export const ITEM_MAX: Partial<Record<ShopHomeKind, number>> = {
    hero_slide: 6,
    home_tab: 5,
    promo: 3,
}
export const STORY_IMAGE_MAX = 3
export const STORY_STAT_MAX = 3
export const OCOP_CERT_MAX = 12
export const BRANCH_MAX = 12
export const STORE_MAX = 30
export const PHONE_MAX = 5
export const LINK_CATEGORIES_MAX = 10
const LINK_QUERY_MAX = 100
const SLUG_MAX = 80
const URL_MAX = 500

const SLUG_RE = /^[a-z0-9]+(-[a-z0-9]+)*$/
const HTTP_RE = /^https?:\/\/\S+$/
const PHONE_RE = /^\+?[0-9][0-9 .]{6,18}[0-9]$/

export const isListKind = (k: ShopHomeKind) => k in ITEM_MAX
/** Khối chỉ có trên web: server luôn ép show_miniapp = false. */
export const isWebOnly = (k: ShopHomeKind) => k === 'home_tab' || k === 'story'
/** Lịch hiện theo dòng: slide + khuyến mãi. */
export const hasItemSchedule = (k: ShopHomeKind) =>
    k === 'hero_slide' || k === 'promo'
/** Lịch hiện của cả khối: chỉ banner quà biếu. */
export const hasBlockSchedule = (k: ShopHomeKind) => k === 'gift_banner'

// Ô chữ theo khối: [tên, tối đa, bắt buộc, nhiều dòng].
type TextSpec = [name: string, max: number, required?: boolean, block?: boolean]

const TEXT: Record<ShopHomeKind, TextSpec[]> = {
    hero_slide: [
        ['tab', 40, true],
        ['tab_sub', 80],
        ['badge', 60],
        ['badge_mobile', 30],
        ['title', 200, true, true],
        ['description', 500, false, true],
        ['image_alt', 200],
        ['cta_label', 40, true],
        ['cta_label_mobile', 30],
    ],
    home_tab: [
        ['title', 60, true],
        ['subtitle', 120],
    ],
    promo: [
        ['tag', 40],
        ['title', 120, true],
        ['subtitle', 200],
    ],
    gift_banner: [
        ['title', 120],
        ['subtitle', 200],
        ['cta_label', 40],
        ['mobile_title', 120],
        ['mobile_subtitle', 200],
        ['mobile_cta_label', 30],
    ],
    story: [
        ['eyebrow', 60],
        ['title', 160],
        ['body', 3000, false, true],
        ['cta_label', 40],
    ],
    ocop: [
        ['title', 160],
        ['subtitle', 500, false, true],
    ],
    store_system: [],
}

// Ô ảnh theo khối: [tên, bắt buộc].
const IMAGES: Record<ShopHomeKind, [string, boolean][]> = {
    hero_slide: [
        ['image', true],
        ['image_mobile', false],
    ],
    home_tab: [],
    promo: [['image', true]],
    gift_banner: [['image', false]],
    story: [],
    ocop: [['logo', false]],
    store_system: [],
}

/** Khối có ô link; hero_slide bắt buộc, còn lại tuỳ chọn. */
const LINKED: ShopHomeKind[] = [
    'hero_slide',
    'home_tab',
    'promo',
    'gift_banner',
    'story',
]

type Obj = Record<string, unknown>

const obj = (v: unknown): Obj =>
    v && typeof v === 'object' && !Array.isArray(v) ? (v as Obj) : {}
const str = (v: unknown) => (typeof v === 'string' ? v : '')
const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : [])
const flag = (v: unknown) => v !== false

export function imageIn(v: unknown): ImageValue | null {
    const o = obj(v)
    const url = str(o.url)
    if (!url) return null
    const thumb = str(o.thumb_url)
    return thumb ? { url, thumb_url: thumb } : { url }
}

export function linkIn(v: unknown): LinkForm {
    const o = obj(v)
    const types = arr(o.types).filter((c): c is string => typeof c === 'string')
    return {
        type: str(o.type),
        slug: str(o.slug),
        url: str(o.url),
        phone: str(o.phone),
        q: str(o.q),
        categories: arr(o.categories).filter(
            (c): c is string => typeof c === 'string',
        ),
        ...(types.length > 0 ? { types } : {}),
    }
}

function storeIn(v: unknown): StoreForm {
    const s = obj(v)
    return {
        key: newKey(),
        name: str(s.name),
        address: str(s.address),
        map_url: str(s.map_url),
        phones: arr(s.phones).map(str),
        website: str(s.website),
        show_web: flag(s.show_web),
        show_miniapp: flag(s.show_miniapp),
    }
}

export const emptyStore = () => storeIn({})

function branchIn(v: unknown): BranchForm {
    const b = obj(v)
    return {
        key: newKey(),
        name: str(b.name),
        show_web: flag(b.show_web),
        show_miniapp: flag(b.show_miniapp),
        stores: arr(b.stores).map(storeIn),
    }
}

export const emptyBranch = () => branchIn({})

export function certificateIn(v: unknown): CertificateForm {
    const c = obj(v)
    return {
        key: newKey(),
        image: imageIn(c.image),
        title: str(c.title),
        stars: typeof c.stars === 'number' ? String(c.stars) : '',
        level: str(c.level),
    }
}

export function statIn(v: unknown): StatForm {
    const s = obj(v)
    return { key: newKey(), value: str(s.value), label: str(s.label) }
}

/** data (JSON server) → giá trị form của khối. */
export function dataIn(kind: ShopHomeKind, raw: unknown): Obj {
    const d = obj(raw)
    const out: Obj = {}
    for (const [name] of TEXT[kind]) out[name] = str(d[name])
    for (const [name] of IMAGES[kind]) out[name] = imageIn(d[name])
    if (LINKED.includes(kind)) out.link = linkIn(d.link)
    switch (kind) {
        case 'hero_slide':
            out.image_position = str(d.image_position) || HERO_POSITIONS[0]
            break
        case 'story':
            out.images = arr(d.images).flatMap((i) => {
                const img = imageIn(i)
                return img ? [img] : []
            })
            out.stats = arr(d.stats).map(statIn)
            break
        case 'ocop':
            out.certificates = arr(d.certificates).map(certificateIn)
            break
        case 'store_system': {
            const hq = obj(d.headquarters)
            out.headquarters = {
                title: str(hq.title),
                address: str(hq.address),
                map_url: str(hq.map_url),
                phones: arr(hq.phones).map(str),
                image: imageIn(hq.image),
            }
            out.branches = arr(d.branches).map(branchIn)
            break
        }
    }
    return out
}

const opt = (v: unknown) => {
    const s = str(v)
    return s.trim() === '' ? undefined : s
}

const imageOut = (v: unknown) => {
    const img = v as ImageValue | null | undefined
    return img?.url ? { url: img.url } : undefined
}

/** Link gửi đi: chỉ giữ trường của đúng loại; chưa chọn loại = không có link. */
export function linkOut(l: LinkForm): Obj | undefined {
    switch (l.type) {
        case '':
            return undefined
        case 'products':
            return {
                type: l.type,
                categories: l.categories,
                ...(l.types?.length ? { types: l.types } : {}),
            }
        case 'search':
            return { type: l.type, q: l.q.trim() }
        case 'url':
            return { type: l.type, url: l.url.trim() }
        case 'tel':
        case 'zalo':
            return { type: l.type, phone: l.phone.trim() }
        default:
            return { type: l.type, slug: l.slug.trim() }
    }
}

/** Giá trị form của khối → data gửi server (bỏ key / thumb_url / ô trống). */
export function dataOut(kind: ShopHomeKind, d: Obj): Obj {
    const out: Obj = {}
    for (const [name, , required] of TEXT[kind])
        out[name] = required ? str(d[name]) : opt(d[name])
    for (const [name] of IMAGES[kind]) out[name] = imageOut(d[name])
    if (LINKED.includes(kind)) out.link = linkOut(d.link as LinkForm)
    switch (kind) {
        case 'hero_slide':
            out.image_position = d.image_position
            break
        case 'story':
            out.images = (d.images as ImageValue[]).map((i) => ({
                url: i.url,
            }))
            out.stats = (d.stats as StatForm[]).map((s) => ({
                value: s.value,
                label: s.label,
            }))
            break
        case 'ocop':
            out.certificates = (d.certificates as CertificateForm[]).map(
                (c) => ({
                    image: imageOut(c.image),
                    title: c.title,
                    stars: c.stars ? Number(c.stars) : undefined,
                    level: c.level || undefined,
                }),
            )
            break
        case 'store_system': {
            const hq = obj(d.headquarters)
            out.headquarters = {
                title: opt(hq.title),
                address: opt(hq.address),
                map_url: opt(hq.map_url),
                // Gửi cả ô trống: server bỏ qua ô trống nhưng giữ chỉ số,
                // nên lỗi phones.N trỏ đúng ô trên form.
                phones: hq.phones,
                image: imageOut(hq.image),
            }
            out.branches = (d.branches as BranchForm[]).map((b) => ({
                name: b.name,
                show_web: b.show_web,
                show_miniapp: b.show_miniapp,
                stores: b.stores.map((s) => ({
                    name: s.name,
                    address: s.address,
                    map_url: opt(s.map_url),
                    phones: s.phones,
                    website: opt(s.website),
                    show_web: s.show_web,
                    show_miniapp: s.show_miniapp,
                })),
            }))
            break
        }
    }
    return out
}

/** ISO → giá trị ô datetime-local (giờ máy). */
export const toLocalInput = (iso: string | null) =>
    iso ? format(new Date(iso), "yyyy-MM-dd'T'HH:mm") : ''

/** Ô datetime-local → ISO; trống = null (luôn hiện). */
export const toIso = (local: string) =>
    local ? new Date(local).toISOString() : null

export function newItem(kind: ShopHomeKind): ItemForm {
    return {
        key: newKey(),
        id: null,
        enabled: true,
        show_web: true,
        show_miniapp: !isWebOnly(kind),
        starts_at: '',
        ends_at: '',
        data: dataIn(kind, {}),
    }
}

export function blockToForm(s: ShopHomeSection): BlockForm {
    return {
        enabled: s.enabled,
        show_web: s.show_web,
        show_miniapp: s.show_miniapp,
        starts_at: toLocalInput(s.starts_at),
        ends_at: toLocalInput(s.ends_at),
        data: isListKind(s.kind) ? {} : dataIn(s.kind, s.data),
        items: (s.items ?? []).map((it) => ({
            key: newKey(),
            id: it.id,
            enabled: it.enabled,
            show_web: it.show_web,
            show_miniapp: it.show_miniapp,
            starts_at: toLocalInput(it.starts_at),
            ends_at: toLocalInput(it.ends_at),
            data: dataIn(s.kind, it.data),
        })),
    }
}

export function blockToInput(
    kind: ShopHomeKind,
    version: number,
    f: BlockForm,
): ShopHomeSectionInput {
    const webOnly = isWebOnly(kind)
    const input: ShopHomeSectionInput = {
        version,
        enabled: f.enabled,
        show_web: f.show_web,
        show_miniapp: webOnly ? false : f.show_miniapp,
    }
    if (hasBlockSchedule(kind)) {
        input.starts_at = toIso(f.starts_at)
        input.ends_at = toIso(f.ends_at)
    }
    if (!isListKind(kind)) {
        input.data = dataOut(kind, f.data)
        return input
    }
    input.items = f.items.map((it) => {
        const row: ShopHomeItemInput = {
            enabled: it.enabled,
            show_web: it.show_web,
            show_miniapp: webOnly ? false : it.show_miniapp,
            data: dataOut(kind, it.data),
        }
        if (it.id) row.id = it.id
        if (hasItemSchedule(kind)) {
            row.starts_at = toIso(it.starts_at)
            row.ends_at = toIso(it.ends_at)
        }
        return row
    })
    return input
}

// ---- Kiểm nhanh ở FE (server vẫn kiểm đủ), lỗi gắn đúng đường dẫn ô ----

export interface Issue {
    path: string
    message: string
}

const runes = (s: string) => [...s].length
const line = (s: string) => s.trim().split(/\s+/).filter(Boolean).join(' ')

class Checker {
    issues: Issue[] = []
    fail(path: string, message: string) {
        this.issues.push({ path, message })
    }
    text(
        path: string,
        v: unknown,
        max: number,
        required = false,
        block = false,
    ) {
        const s = block ? str(v).trim() : line(str(v))
        if (!s) {
            if (required) this.fail(path, m.error_shop_field_required())
        } else if (runes(s) > max) {
            this.fail(path, m.error_shop_field_too_long({ max: String(max) }))
        }
    }
    image(path: string, v: unknown, required: boolean) {
        if (required && !imageIn(v))
            this.fail(path, m.error_shop_field_required())
    }
    count(path: string, n: number, max: number) {
        if (n > max)
            this.fail(path, m.error_shop_field_too_many({ max: String(max) }))
    }
    http(path: string, v: unknown, required = false) {
        const s = str(v).trim()
        if (!s) {
            if (required) this.fail(path, m.error_shop_field_required())
        } else if (s.length > URL_MAX || !HTTP_RE.test(s)) {
            this.fail(path, m.error_shop_field_invalid())
        }
    }
    phone(path: string, v: unknown, required = false) {
        const s = line(str(v))
        if (!s) {
            if (required) this.fail(path, m.error_shop_field_required())
        } else if (!PHONE_RE.test(s)) {
            this.fail(path, m.error_shop_field_invalid())
        }
    }
    phones(path: string, v: unknown) {
        const list = arr(v).map(str)
        list.forEach((p, i) => this.phone(`${path}.${i}`, p))
        this.count(path, list.filter((p) => p.trim()).length, PHONE_MAX)
    }
    slug(path: string, v: string) {
        const s = v.trim()
        if (!s) this.fail(path, m.error_shop_field_required())
        else if (s.length > SLUG_MAX || !SLUG_RE.test(s))
            this.fail(path, m.error_shop_field_invalid())
    }
    link(path: string, l: LinkForm, required: boolean) {
        switch (l.type) {
            case '':
                if (required)
                    this.fail(`${path}.type`, m.error_shop_field_required())
                return
            case 'search':
                this.text(`${path}.q`, l.q, LINK_QUERY_MAX, true)
                return
            case 'products':
                // Danh mục và / hoặc loại sản phẩm — cần ít nhất một.
                if (l.categories.length === 0 && !l.types?.length)
                    this.fail(
                        `${path}.categories`,
                        m.error_shop_field_required(),
                    )
                this.count(
                    `${path}.categories`,
                    l.categories.length,
                    LINK_CATEGORIES_MAX,
                )
                return
            case 'url':
                this.http(`${path}.url`, l.url, true)
                return
            case 'tel':
            case 'zalo':
                this.phone(`${path}.phone`, l.phone, true)
                return
            default:
                this.slug(`${path}.slug`, l.slug)
        }
    }
    schedule(path: string, from: string, to: string) {
        if (from && to && new Date(from) >= new Date(to))
            this.fail(
                path ? `${path}.ends_at` : 'ends_at',
                m.shop_home_schedule_invalid(),
            )
    }
}

function checkData(c: Checker, kind: ShopHomeKind, d: Obj, p: string) {
    for (const [name, max, required, block] of TEXT[kind])
        c.text(`${p}.${name}`, d[name], max, required, block)
    for (const [name, required] of IMAGES[kind])
        c.image(`${p}.${name}`, d[name], required)
    if (LINKED.includes(kind))
        c.link(`${p}.link`, d.link as LinkForm, kind === 'hero_slide')
    switch (kind) {
        case 'story':
            c.count(
                `${p}.images`,
                (d.images as unknown[]).length,
                STORY_IMAGE_MAX,
            )
            c.count(`${p}.stats`, (d.stats as unknown[]).length, STORY_STAT_MAX)
            ;(d.stats as StatForm[]).forEach((s, i) => {
                c.text(`${p}.stats.${i}.value`, s.value, 20, true)
                c.text(`${p}.stats.${i}.label`, s.label, 60, true)
            })
            break
        case 'ocop': {
            const certs = d.certificates as CertificateForm[]
            c.count(`${p}.certificates`, certs.length, OCOP_CERT_MAX)
            certs.forEach((cert, i) => {
                c.image(`${p}.certificates.${i}.image`, cert.image, true)
                c.text(`${p}.certificates.${i}.title`, cert.title, 160, true)
            })
            break
        }
        case 'store_system': {
            const hq = obj(d.headquarters)
            const hp = `${p}.headquarters`
            c.text(`${hp}.title`, hq.title, 160)
            c.text(`${hp}.address`, hq.address, 300, false, true)
            c.http(`${hp}.map_url`, hq.map_url)
            c.phones(`${hp}.phones`, hq.phones)
            const branches = d.branches as BranchForm[]
            c.count(`${p}.branches`, branches.length, BRANCH_MAX)
            branches.forEach((b, i) => {
                const bp = `${p}.branches.${i}`
                c.text(`${bp}.name`, b.name, 120, true)
                c.count(`${bp}.stores`, b.stores.length, STORE_MAX)
                b.stores.forEach((s, j) => {
                    const sp = `${bp}.stores.${j}`
                    c.text(`${sp}.name`, s.name, 120, true)
                    c.text(`${sp}.address`, s.address, 300, true, true)
                    c.http(`${sp}.map_url`, s.map_url)
                    c.phones(`${sp}.phones`, s.phones)
                    c.http(`${sp}.website`, s.website)
                })
            })
            break
        }
    }
}

export function validateBlock(kind: ShopHomeKind, f: BlockForm): Issue[] {
    const c = new Checker()
    if (hasBlockSchedule(kind)) c.schedule('', f.starts_at, f.ends_at)
    if (!isListKind(kind)) {
        checkData(c, kind, f.data, 'data')
        return c.issues
    }
    c.count('items', f.items.length, ITEM_MAX[kind] ?? 0)
    f.items.forEach((it, i) => {
        checkData(c, kind, it.data, `items.${i}.data`)
        if (hasItemSchedule(kind))
            c.schedule(`items.${i}`, it.starts_at, it.ends_at)
    })
    return c.issues
}

export const blockSchema = (kind: ShopHomeKind) =>
    z.custom<BlockForm>().superRefine((f, ctx) => {
        for (const { path, message } of validateBlock(kind, f))
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: path.split('.'),
                message,
            })
    })

/** Chỉ số dòng từ đường dẫn lỗi server (items.3.data.title → 3). */
export function itemIndexOf(field: unknown): number | null {
    const hit = typeof field === 'string' && /^items\.(\d+)\./.exec(field)
    return hit ? Number(hit[1]) : null
}

// ---- Tóm tắt cho danh sách khối / danh sách dòng ----

export function kindLabel(kind: ShopHomeKind): string {
    switch (kind) {
        case 'hero_slide':
            return m.shop_home_kind_hero_slide()
        case 'home_tab':
            return m.shop_home_kind_home_tab()
        case 'promo':
            return m.shop_home_kind_promo()
        case 'gift_banner':
            return m.shop_home_kind_gift_banner()
        case 'story':
            return m.shop_home_kind_story()
        case 'ocop':
            return m.shop_home_kind_ocop()
        case 'store_system':
            return m.shop_home_kind_store_system()
    }
}

/** Số dòng ngắn dưới tên khối: "4/6 slide", "8 giấy", "2 chi nhánh · 9 cửa hàng". */
export function blockCount(s: ShopHomeSection): string {
    const n = (s.items ?? []).length
    const max = String(ITEM_MAX[s.kind] ?? 0)
    const d = obj(s.data)
    switch (s.kind) {
        case 'hero_slide':
            return m.shop_home_count_slides({ n: String(n), max })
        case 'home_tab':
        case 'promo':
            return m.shop_home_count_rows({ n: String(n), max })
        case 'gift_banner':
            return imageIn(d.image)
                ? m.shop_home_count_has_image()
                : m.shop_home_count_no_image()
        case 'story':
            return m.shop_home_web_only()
        case 'ocop':
            return m.shop_home_count_certificates({
                n: String(arr(d.certificates).length),
            })
        case 'store_system': {
            const branches = arr(d.branches)
            const stores = branches.reduce<number>(
                (sum, b) => sum + arr(obj(b).stores).length,
                0,
            )
            return m.shop_home_count_stores({
                branches: String(branches.length),
                stores: String(stores),
            })
        }
    }
}

/** Kênh của khối: on = đang bật + có cờ kênh, none = kênh không có khối này. */
export function channelState(
    s: ShopHomeSection,
    channel: 'web' | 'miniapp',
): 'on' | 'off' | 'none' {
    if (channel === 'miniapp' && isWebOnly(s.kind)) return 'none'
    const shown = channel === 'web' ? s.show_web : s.show_miniapp
    return s.enabled && shown ? 'on' : 'off'
}

const day = (d: Date) => format(d, 'dd/MM')
const fullDay = (d: Date) => format(d, 'dd/MM/yyyy')

/** "Luôn hiện" / "01/09 – 30/09/2026 · đã hết lịch"; expired = mờ dòng. */
export function scheduleText(
    from: string,
    to: string,
    now: Date,
): { text: string; expired: boolean } {
    if (!from && !to) return { text: m.shop_home_always(), expired: false }
    const start = from ? new Date(from) : null
    const end = to ? new Date(to) : null
    let text: string
    if (start && end)
        text = `${start.getFullYear() === end.getFullYear() ? day(start) : fullDay(start)} – ${fullDay(end)}`
    else if (start) text = m.shop_home_schedule_from({ date: fullDay(start) })
    else text = m.shop_home_schedule_to({ date: fullDay(end!) })
    const expired = !!end && end < now
    if (expired) return { text: `${text} · ${m.shop_home_expired()}`, expired }
    if (start && start > now)
        return { text: `${text} · ${m.shop_home_not_started()}`, expired }
    return { text, expired }
}

export function linkTypeLabel(type: string): string {
    switch (type) {
        case 'category':
            return m.shop_home_link_category()
        case 'group':
            return m.shop_home_link_group()
        case 'products':
            return m.shop_home_link_products()
        case 'search':
            return m.shop_home_link_search()
        case 'post':
            return m.shop_home_link_post()
        case 'page':
            return m.shop_home_link_page()
        case 'url':
            return m.shop_home_link_url()
        case 'tel':
            return m.shop_home_link_tel()
        case 'zalo':
            return m.shop_home_link_zalo()
        default:
            return type
    }
}

export interface CatalogNames {
    categories: Map<string, string>
    groups: Map<string, string>
}

/** "Danh mục › Chè Tôm Nõn" — tên lấy từ danh sách danh mục / nhóm nếu có. */
export function linkSummary(l: LinkForm, names: CatalogNames): string {
    if (!l.type) return m.shop_home_link_none()
    let target: string
    switch (l.type) {
        case 'category':
            target = names.categories.get(l.slug) ?? l.slug
            break
        case 'group':
            target = names.groups.get(l.slug) ?? l.slug
            break
        case 'products':
            target = [
                ...l.categories.map((c) => names.categories.get(c) ?? c),
                ...(l.types ?? []).map(productTypeLabel),
            ].join(', ')
            break
        case 'search':
            target = l.q
            break
        case 'url':
            target = l.url
            break
        case 'tel':
        case 'zalo':
            target = l.phone
            break
        default:
            target = l.slug
    }
    return `${linkTypeLabel(l.type)} › ${target}`
}

export function heroPositionLabel(p: string): string {
    switch (p) {
        case 'center top':
            return m.shop_home_pos_top()
        case 'center 35%':
            return m.shop_home_pos_upper()
        case 'center bottom':
            return m.shop_home_pos_bottom()
        case 'left center':
            return m.shop_home_pos_left()
        case '70% center':
            return m.shop_home_pos_right_slight()
        case 'right center':
            return m.shop_home_pos_right()
        default:
            return m.shop_home_pos_center()
    }
}
